import React from 'react';
import { createRoot } from 'react-dom/client';
import html2pdf from 'html2pdf.js';
import RoomQuotationTemplate from './RoomQuotationTemplate';
import { getCompanySettings } from '../pages/verve-settings/services/settingsService';
import { DEFAULT_COMPANY_INFO } from '../pages/verve-settings/constants/settingsTabs';
import { API_BASE_URL as BASE_URL } from '../services/api';

export const getQuotationData = (leadData) => {
  return {
    projectName: leadData.projectName || "-",
    clientName: leadData.customerName || `${leadData.firstName || ''} ${leadData.lastName || ''}`.trim() || "-",
    date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    site: leadData.city || leadData.location || "-",
    quotationNo: leadData.customId ? `VK/${leadData.customId.toUpperCase()}` : `VK/${Date.now().toString().slice(-6)}`,
    contactRef: leadData.phoneNumber || leadData.phone || "",
    designer: leadData.projectManager?.name || leadData.assignedTo?.name || "Srirupa",
    rooms: [
      {
        roomName: "Initial Estimate",
        items: [
          {
            name: "MAGIC NIGHT - AD-104-2",
            colour: "As per design",
            type: "Marine club",
            warranty: "7 years of warranty",
            amount: "67,500.00",
            details: [
              "Full slab : 51 sft",
              "- First floor powder room",
              "-Kid's bathroom",
              "-Second floor master bathroom",
              "-Second floor Home theater"
            ]
          },
          {
            name: "HAFELE : FOSSILE - 564:42:163",
            colour: "As per design",
            type: "Marine club",
            warranty: "7 years of warranty",
            amount: "1,23,649.00",
            details: [
              "Full slab : 54 sft",
              "- First floor guest room",
              "-First floor master bathroom"
            ]
          },
          {
            name: "LAVENDER CREST",
            colour: "As per design",
            type: "Marine club",
            warranty: "7 years of warranty",
            amount: "1,22,400.00",
            details: [
              "Full slab : 51 sft",
              "Slab qty : 2"
            ]
          },
          {
            name: "CONSOLE UNIT",
            colour: "As per design",
            type: "Marine club",
            warranty: "7 years of warranty",
            amount: "97,000.00",
            details: [
              "Base unit : Fully modular with carcass as 18 mm thickness,",
              "internal as linear lamiante.",
              "Shutters & exposed facia with PU finish.",
              "Handles : gola profile",
              "",
              "Hardware : Blum"
            ]
          },
          {
            name: "POOJA UNIT",
            colour: "As per design",
            type: "Marine club",
            warranty: "7 years of warranty",
            amount: "1,17,600.00",
            details: [
              "Base unit : Fully modular with carcass as 18 mm thickness,",
              "internal as linear lamiante.",
              "Shutters & exposed facia with PU finish.",
              "Handles : gola profile",
              "Pooja Doors : Profile with Bells .",
              "",
              "Hardware : Blum"
            ]
          },
          {
            name: "APPLIANCE",
            colour: "As per design",
            type: "Marine club",
            warranty: "7 years of warranty",
            amount: "42,490.00",
            details: [
              "Brass burners to sear, simmer and pan fry.",
              "Blue flame burns efficiently to lower energy cost.",
              "Mini triple-ring burner for intense, even heat.",
              "ELECTROLUX : EHG643OBC"
            ]
          }
        ]
      }
    ],
    totals: {
      total: "16,99,830.00",
      discount: "2,00,000.00",
      grandTotal: "14,99,830.00"
    }
  };
};

export const generateQuotationPdf = (leadData, customQuotationData = null) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Create a temporary container
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.top = '-9999px';
      container.style.left = '-9999px';
      container.style.width = '800px'; // Force width for perfect A4 scaling
      document.body.appendChild(container);

      // Map lead data to quotation format
      const quotationData = customQuotationData || getQuotationData(leadData);

      // Pre-fetch latest company info right before rendering PDF
      try {
        const res = await getCompanySettings();
        const payload = res?.data?.data || res?.data || {};
        const formatLogoUrl = (path) => {
          if (!path || path === "undefined" || path === "null") return "";
          if (path.startsWith("http") || path.startsWith("data:")) return path;
          let cleanPath = path.replace(/\\/g, '/');
          cleanPath = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
          return `${BASE_URL}${cleanPath}`;
        };
        quotationData.company = {
          name: payload.companyName || DEFAULT_COMPANY_INFO.name,
          address: payload.companyAddress || DEFAULT_COMPANY_INFO.address,
          logoUrl: formatLogoUrl(payload.logoExpandedPath),
        };
      } catch (e) {
        console.error("Error loading company settings for PDF generation:", e);
      }

      // Render the React component into the container
      const root = createRoot(container);
      root.render(<RoomQuotationTemplate data={quotationData} />);

      // Wait a moment for React to render and images to load
      setTimeout(() => {
        const element = container.firstChild;
        
        const opt = {
          margin: 10,
          filename: `Quotation_${quotationData.clientName.replace(/\s+/g, '_')}_${quotationData.quotationNo}.pdf`,
          image: { type: 'jpeg', quality: 1 },
          html2canvas: { scale: 2, useCORS: true, logging: false, windowWidth: 800, width: 800 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().from(element).set(opt).save().then(() => {
          // Cleanup
          root.unmount();
          document.body.removeChild(container);
          resolve();
        }).catch(err => {
          root.unmount();
          document.body.removeChild(container);
          reject(err);
        });
      }, 500); // 500ms delay to ensure rendering and image loading
    } catch (error) {
      reject(error);
    }
  });
};
